import emoji from 'emoji-dictionary';
import matter from 'gray-matter';
import { useRouter } from 'next/router';
import React from 'react';
import htmlParser from 'react-markdown/plugins/html-parser';
import ReactMarkdown from 'react-markdown/with-html';
import removeMd from 'remove-markdown';

import styles from '../../../css/markdown.module.css';
import sqlTemplatePositions from '../../../post-positions/sql-templates';

import AsideSqlTemplates from '@components/AsideSqlTemplates';
import CodeBlock from '@components/CodeBlock';
import ContainerSidebar from '@components/ContainerSidebar';
import CTA from '@components/CTA';
import CustomHead from '@components/CustomHead';
import Footer from '@components/FooterNew';
import Header from '@components/HeaderNew';
import HeadingRenderer from '@components/HeadingRenderer';
import ImageRenderer from '@components/ImageRenderer';
import Share from '@components/Share';

function SqlTemplates({ data, posts }) {
  const router = useRouter();

  const content = JSON.parse(data);

  const getSummary = (source) => {
    const stripped = removeMd(source, {
      stripListLeaders: true,
      listUnicodeChar: '',
      gfm: true,
    });
    const lines = stripped.split('\n');

    // print the first few lines...
    return `${lines[0]} ${lines[1]} ${lines[2]} ${lines[3]}`;
  };

  const cleanPost = {
    title: content?.data.title,
    body: content?.content,
    category: content?.category,
    summary: getSummary(content?.content),
  };

  const parseHtml = htmlParser({
    isValidNode: (node) => node.type !== 'script',
  });

  const emojiSupport = (text) =>
    text.value.replace(/:\w+:/gi, (name) => emoji.getUnicode(name));

  return (
    <>
      <CustomHead
        meta={{
          title: `${cleanPost.title} - PopSQL`,
          image: `${process.env.NEXT_PUBLIC_ASSET_HOST}/static/images/og/sql-templates.jpg`, // TODO
          description: `${cleanPost.summary}`,
        }}
        openGraph={{
          title: `${cleanPost.title} - PopSQL`,
          url: `${process.env.NEXT_PUBLIC_WEB_HOST}${router.asPath}`,
          description: `${cleanPost.summary}`,
        }}
      />
      <main>
        <Header />
        <ContainerSidebar
          dbName=""
          postTitle={cleanPost.title}
          asideComponent={<AsideSqlTemplates data={posts} />}
        >
          <div className={styles.popsql__markdown}>
            <h1>{cleanPost.title}</h1>
          </div>
          <ReactMarkdown
            className={styles.popsql__markdown}
            source={cleanPost.body}
            escapeHtml={false}
            renderers={{
              code: CodeBlock,
              heading: HeadingRenderer,
              image: ImageRenderer,
              text: emojiSupport,
            }}
            astPlugins={[parseHtml]}
          />
          <Share articleName={cleanPost.title} feedback={false} />
        </ContainerSidebar>
        <CTA />
      </main>
      <Footer />
    </>
  );
}

export async function getStaticPaths() {
  const fs = require('fs');
  const path = require('path');
  const paths = [];
  const regex = /^data|\.md$/g;

  const getAllFiles = (dir) =>
    fs.readdirSync(dir).reduce((files, file) => {
      const name = path.join(dir, file);
      const isDirectory = fs.statSync(name).isDirectory();
      return isDirectory ? [...files, ...getAllFiles(name)] : [...files, name];
    }, []);

  const preprocessed = getAllFiles('./data/sql-templates');

  preprocessed.forEach((preprocessedPath) => {
    paths.push(preprocessedPath.replace(regex, ''));
    // Workaround to use trailing slash on generated paths
    paths.push(preprocessedPath.replace(regex, '') + '/');
  });

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps(ctx) {
  const posts = await ((context) => {
    const keys = context.keys();
    const values = keys.map(context);
    const data = keys.map((key, index) => {
      // Create id from filename
      const id = key.split('/')[1];
      const value = values[index];
      // Parse yaml metadata & markdownbody in document
      const document = matter(value.default);
      const post_information = JSON.stringify(document.data);

      // Create slug from filename
      const slug = post_information.url
        ? post_information.url
        : key
            .replace(/^.*[\\/]/, '')
            .split('.')
            .slice(0, -1)
            .join('.');

      return {
        post_information,
        slug,
        id,
        position: sqlTemplatePositions.indexOf(slug),
        isDraft: document.data.draft === undefined ? true : document.data.draft,
      };
    });
    return data.filter((post) => !post.isDraft);
  })(require.context('../../../data/sql-templates', true, /\.md$/)).sort(
    (a, b) => (a.position < b.position ? -1 : 1),
  );

  const { id, slug } = ctx.params;
  const content = await import(`../../../data/sql-templates/${id}/${slug}.md`);
  const data = JSON.stringify({
    ...matter(content.default),
    category: id,
  });

  return {
    props: {
      posts,
      data,
    },
  };
}

export default SqlTemplates;
